import { useEffect, useMemo, useState } from "react";
import api, { fmtBRL, fmtBRLSmart, fmtInt, fmtNum, fmtPct } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Pencil, Trash2, X, Save, LayoutGrid, Rows3, ChevronDown, ChevronRight, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const empty = () => ({
  id: null,
  code: "",
  name: "",
  category: "",
  yield_quantity: 1,
  yield_unit: "UN",
  sale_price: 0,
  ingredients: [],
  notes: "",
});

export default function Recipes() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // "table" | "cards"
  const [expanded, setExpanded] = useState({}); // recipe.id -> bool (cards view)
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [webhookOk, setWebhookOk] = useState(true);

  useEffect(() => {
    api.get("/webhooks/config")
      .then(r => setWebhookOk(!!(r.data?.target_url || "").trim()))
      .catch(() => setWebhookOk(true));
  }, []);

  const syncOmie = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/sync/omie/recipes");
      toast.success(`Omie: ${r.data.count} fichas sincronizadas (${r.data.with_ingredients} com ingredientes)`);
      load();
    } catch (e) {
      toast.error(`Erro: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/recipes").then(r => setList(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return list.filter(r => {
      if (categoryFilter !== "all" && (r.category || "Sem categoria") !== categoryFilter) return false;
      if (!s) return true;
      return (r.name || "").toLowerCase().includes(s) || (r.code || "").toString().includes(s);
    });
  }, [list, search, categoryFilter]);

  const categories = useMemo(() => {
    const set = new Map();
    list.forEach(r => {
      const c = r.category || "Sem categoria";
      set.set(c, (set.get(c) || 0) + 1);
    });
    return Array.from(set.entries()).map(([name, count]) => ({ name, count }));
  }, [list]);

  // Group filtered recipes by category (for the "cards" view)
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const c = r.category || "Sem categoria";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(r);
    });
    return Array.from(map.entries()).map(([category, recipes]) => ({
      category,
      recipes: recipes.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      total: recipes.length,
    })).sort((a, b) => a.category.localeCompare(b.category));
  }, [filtered]);

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const newRecipe = () => setEditing(empty());
  const editRecipe = (r) => setEditing({
    ...empty(),
    ...r,
    ingredients: (r.ingredients || []).map(i => ({ ...i })),
  });

  const save = async () => {
    if (!editing.name) {
      toast.error("Informe o nome do prato");
      return;
    }
    try {
      const payload = {
        code: editing.code || "",
        name: editing.name,
        category: editing.category || "",
        yield_quantity: Number(editing.yield_quantity) || 1,
        yield_unit: editing.yield_unit || "UN",
        sale_price: Number(editing.sale_price) || 0,
        ingredients: (editing.ingredients || []).map(i => ({
          code: i.code || "",
          name: i.name || "",
          quantity: Number(i.quantity) || 0,
          unit: i.unit || "",
          unit_cost: Number(i.unit_cost) || 0,
          line_cost: 0,
        })),
        notes: editing.notes || "",
      };
      if (editing.id) {
        await api.put(`/recipes/${editing.id}`, payload);
        toast.success("Ficha atualizada");
      } else {
        await api.post("/recipes", payload);
        toast.success("Ficha criada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erro ao salvar ficha");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Excluir ficha "${r.name}"?`)) return;
    await api.delete(`/recipes/${r.id}`);
    toast.success("Ficha excluída");
    load();
  };

  // ingredient helpers
  const addIngredient = () => setEditing(e => ({
    ...e,
    ingredients: [...(e.ingredients || []), { code: "", name: "", quantity: 0, unit: "", unit_cost: 0 }],
  }));
  const updateIngredient = (idx, field, val) => setEditing(e => {
    const arr = [...e.ingredients];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...e, ingredients: arr };
  });
  const removeIngredient = (idx) => setEditing(e => {
    const arr = [...e.ingredients];
    arr.splice(idx, 1);
    return { ...e, ingredients: arr };
  });

  const totalCost = useMemo(() => {
    if (!editing) return 0;
    return (editing.ingredients || []).reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_cost) || 0), 0);
  }, [editing]);
  const cmvPct = useMemo(() => {
    if (!editing || !editing.sale_price) return 0;
    return (totalCost / Number(editing.sale_price)) * 100;
  }, [editing, totalCost]);

  return (
    <div data-testid="recipes-page">
      <PageHeader
        eyebrow="Fichas Técnicas"
        title="RECEITAS E CMV"
        accent="o coração da cozinha."
        description="Cadastre ingredientes, rendimento e preço para acompanhar o CMV teórico de cada prato."
        actions={
          <>
            <button onClick={syncOmie} className="ifl-btn-outline" disabled={syncing} data-testid="sync-recipes-btn">
              <RefreshCw size={14} strokeWidth={2} className={syncing ? "ifl-pulse" : ""} />
              {syncing ? "Sincronizando…" : "Sincronizar do Omie"}
            </button>
            <button onClick={newRecipe} className="ifl-btn" data-testid="new-recipe-btn">
              <Plus size={14} strokeWidth={2} /> Nova ficha
            </button>
          </>
        }
        testId="recipes-header"
      />

      {!webhookOk && (
        <div
          className="ifl-card p-4 mb-6 flex items-start gap-3 ifl-fade-up"
          style={{ borderColor: "var(--ifl-mustard)" }}
          data-testid="webhook-warning"
        >
          <AlertTriangle size={18} strokeWidth={1.5} style={{ color: "var(--ifl-mustard)", flexShrink: 0, marginTop: 2 }} />
          <div className="flex-1">
            <div className="heading-caps text-xs mb-1">Webhook desconfigurado</div>
            <div className="font-body text-sm" style={{ color: "var(--ifl-muted)" }}>
              Fichas criadas/editadas <strong style={{ color: "var(--ifl-text)" }}>não estão sendo notificadas</strong> ao seu agente externo. Vá em{" "}
              <Link to="/configuracoes" className="underline" style={{ color: "var(--ifl-text)" }}>
                Configurações → Webhooks
              </Link>{" "}
              e cadastre a URL.
            </div>
          </div>
        </div>
      )}

      <div className="ifl-card p-4 mb-6 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar prato..."
          className="ifl-input w-full md:w-96"
          data-testid="recipe-search"
        />
        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="flex gap-1.5 items-center">
            <button
              onClick={() => setCategoryFilter("all")}
              className={categoryFilter === "all" ? "ifl-btn" : "ifl-btn-outline"}
              data-testid="recipe-cat-all"
            >
              Todas <span style={{ opacity: 0.6, marginLeft: 4 }}>{fmtInt(list.length)}</span>
            </button>
            {categories.map(c => (
              <button
                key={c.name}
                onClick={() => setCategoryFilter(c.name)}
                className={categoryFilter === c.name ? "ifl-btn" : "ifl-btn-outline"}
                data-testid={`recipe-cat-${c.name}`}
              >
                {c.name} <span style={{ opacity: 0.6, marginLeft: 4 }}>{fmtInt(c.count)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex border rounded-full overflow-hidden" style={{ borderColor: "var(--ifl-border)" }}>
          <button
            onClick={() => setViewMode("table")}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display uppercase tracking-widest"
            style={{
              background: viewMode === "table" ? "var(--ifl-text)" : "transparent",
              color: viewMode === "table" ? "#fff" : "var(--ifl-text)",
            }}
            data-testid="view-mode-table"
          >
            <Rows3 size={14} strokeWidth={1.5} /> Tabela
          </button>
          <button
            onClick={() => setViewMode("cards")}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display uppercase tracking-widest"
            style={{
              background: viewMode === "cards" ? "var(--ifl-text)" : "transparent",
              color: viewMode === "cards" ? "#fff" : "var(--ifl-text)",
            }}
            data-testid="view-mode-cards"
          >
            <LayoutGrid size={14} strokeWidth={1.5} /> Por prato
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
      <div className="ifl-card overflow-hidden" data-testid="recipes-table-view">
        {loading ? (
          <div className="p-12 text-center font-body" style={{ color: "var(--ifl-muted)" }}>Carregando…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Prato</th>
                  <th>Categoria</th>
                  <th className="text-right">Rendimento</th>
                  <th className="text-right">Preço de venda</th>
                  <th className="text-right">Custo total</th>
                  <th className="text-right">CMV</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`recipe-row-${r.code || r.id}`}>
                    <td className="font-display text-xs">{r.code}</td>
                    <td>{r.name}</td>
                    <td className="font-display text-xs uppercase" style={{ color: "var(--ifl-muted)" }}>{r.category || "—"}</td>
                    <td className="text-right">{fmtNum(r.yield_quantity, 0)} {r.yield_unit}</td>
                    <td className="text-right">{fmtBRL(r.sale_price)}</td>
                    <td className="text-right">{fmtBRL(r.total_cost)}</td>
                    <td className="text-right" style={{ color: r.cmv_pct > 35 ? "var(--ifl-danger)" : r.cmv_pct > 0 ? "var(--ifl-text)" : "var(--ifl-muted)" }}>
                      {r.cmv_pct ? fmtPct(r.cmv_pct) : "—"}
                    </td>
                    <td className="text-right">
                      <button onClick={() => editRecipe(r)} className="p-2 hover:bg-[var(--ifl-bg)] rounded-lg" data-testid={`edit-recipe-${r.id}`}>
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(r)} className="p-2 hover:bg-[var(--ifl-bg)] rounded-lg ml-1" data-testid={`delete-recipe-${r.id}`}>
                        <Trash2 size={14} strokeWidth={1.5} style={{ color: "var(--ifl-danger)" }} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>
                      Nenhuma ficha encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : (
        <div className="flex flex-col gap-8" data-testid="recipes-cards-view">
          {loading ? (
            <div className="ifl-card p-12 text-center font-body" style={{ color: "var(--ifl-muted)" }}>Carregando…</div>
          ) : grouped.length === 0 ? (
            <div className="ifl-card p-12 text-center font-body" style={{ color: "var(--ifl-muted)" }}>Nenhuma ficha encontrada.</div>
          ) : grouped.map(group => (
            <section key={group.category} data-testid={`recipe-group-${group.category}`}>
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="label-caps">{group.category}</div>
                  <div className="font-italic-serif text-2xl mt-1">{group.total} {group.total === 1 ? "ficha" : "fichas"}.</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {group.recipes.map(r => {
                  const isOpen = !!expanded[r.id];
                  const ingredients = r.ingredients || [];
                  const cmvColor = r.cmv_pct > 35 ? "var(--ifl-danger)" : r.cmv_pct > 0 ? "var(--ifl-success)" : "var(--ifl-muted)";
                  return (
                    <article
                      key={r.id}
                      className="ifl-card p-5 flex flex-col gap-3 ifl-fade-up"
                      data-testid={`recipe-card-${r.code || r.id}`}
                    >
                      <header className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display text-[0.65rem] uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>
                            {r.code || "—"}
                          </div>
                          <h3 className="font-display font-semibold text-base mt-0.5 leading-tight truncate" title={r.name}>
                            {r.name}
                          </h3>
                        </div>
                        <div className="flex items-center">
                          <button onClick={() => editRecipe(r)} className="p-1.5 hover:bg-[var(--ifl-bg)] rounded-lg" title="Editar" data-testid={`card-edit-${r.id}`}>
                            <Pencil size={13} strokeWidth={1.5} />
                          </button>
                          <button onClick={() => remove(r)} className="p-1.5 hover:bg-[var(--ifl-bg)] rounded-lg" title="Excluir" data-testid={`card-delete-${r.id}`}>
                            <Trash2 size={13} strokeWidth={1.5} style={{ color: "var(--ifl-danger)" }} />
                          </button>
                        </div>
                      </header>

                      <div className="grid grid-cols-3 gap-2 pt-1">
                        <div>
                          <div className="label-caps" style={{ fontSize: "0.6rem" }}>Rendimento</div>
                          <div className="font-display text-sm mt-1">{fmtNum(r.yield_quantity, 0)} <span style={{ color: "var(--ifl-muted)", fontSize: "0.7rem" }}>{r.yield_unit}</span></div>
                        </div>
                        <div>
                          <div className="label-caps" style={{ fontSize: "0.6rem" }}>Venda</div>
                          <div className="font-display text-sm mt-1">{fmtBRL(r.sale_price)}</div>
                        </div>
                        <div>
                          <div className="label-caps" style={{ fontSize: "0.6rem" }}>CMV</div>
                          <div className="font-display text-sm mt-1" style={{ color: cmvColor }}>
                            {r.cmv_pct ? fmtPct(r.cmv_pct) : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="ifl-divider" />

                      <button
                        onClick={() => toggleExpand(r.id)}
                        className="flex items-center justify-between w-full text-left"
                        data-testid={`toggle-ingredients-${r.id}`}
                      >
                        <span className="label-caps">
                          {ingredients.length > 0 ? `${ingredients.length} ingredientes` : "Sem ingredientes"}
                        </span>
                        {ingredients.length > 0 && (isOpen
                          ? <ChevronDown size={14} strokeWidth={1.5} />
                          : <ChevronRight size={14} strokeWidth={1.5} />)}
                      </button>

                      {isOpen && ingredients.length > 0 && (
                        <ul className="flex flex-col gap-1.5 text-sm font-body">
                          {ingredients.map((i, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between gap-2 py-1.5 border-b last:border-b-0"
                              style={{ borderColor: "rgba(229, 225, 216, 0.6)" }}
                            >
                              <span className="truncate" title={i.name}>{i.name || "—"}</span>
                              <span className="font-display text-xs whitespace-nowrap" style={{ color: "var(--ifl-muted)" }}>
                                {fmtNum(i.quantity || 0, (i.quantity || 0) % 1 === 0 ? 0 : 3)} {i.unit || ""}
                                {i.unit_cost > 0 && (
                                  <span style={{ color: "var(--ifl-text)", marginLeft: 8 }}>
                                    {fmtBRLSmart((Number(i.quantity) || 0) * (Number(i.unit_cost) || 0))}
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                          <li className="flex items-center justify-between pt-2">
                            <span className="label-caps">Custo total</span>
                            <span className="font-display font-semibold text-sm">{fmtBRLSmart(r.total_cost)}</span>
                          </li>
                        </ul>
                      )}

                      {r.notes && isOpen && (
                        <p className="font-italic-serif text-sm" style={{ color: "var(--ifl-muted)" }}>
                          {r.notes}
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15, 27, 45, 0.45)" }} data-testid="recipe-modal">
          <div className="ifl-card w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-caps">{editing.id ? "Editar ficha" : "Nova ficha"}</div>
                <div className="font-italic-serif text-2xl mt-1">{editing.name || "sem nome ainda."}</div>
              </div>
              <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-[var(--ifl-bg)]" data-testid="close-recipe-modal">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <div className="label-caps mb-1">Nome do prato</div>
                <input className="ifl-input w-full" value={editing.name} onChange={(e) => setEditing(s => ({ ...s, name: e.target.value }))} data-testid="recipe-name-input" />
              </div>
              <div>
                <div className="label-caps mb-1">Código</div>
                <input className="ifl-input w-full" value={editing.code} onChange={(e) => setEditing(s => ({ ...s, code: e.target.value }))} data-testid="recipe-code-input" />
              </div>
              <div>
                <div className="label-caps mb-1">Categoria</div>
                <input className="ifl-input w-full" value={editing.category} onChange={(e) => setEditing(s => ({ ...s, category: e.target.value }))} placeholder="Italiana / Asiática..." />
              </div>
              <div>
                <div className="label-caps mb-1">Preço de venda</div>
                <input type="number" step="0.01" className="ifl-input w-full" value={editing.sale_price} onChange={(e) => setEditing(s => ({ ...s, sale_price: e.target.value }))} data-testid="recipe-price-input" />
              </div>
              <div>
                <div className="label-caps mb-1">Rendimento</div>
                <input type="number" className="ifl-input w-full" value={editing.yield_quantity} onChange={(e) => setEditing(s => ({ ...s, yield_quantity: e.target.value }))} />
              </div>
              <div>
                <div className="label-caps mb-1">Unidade de rendimento</div>
                <input className="ifl-input w-full" value={editing.yield_unit} onChange={(e) => setEditing(s => ({ ...s, yield_unit: e.target.value }))} placeholder="UN / KG / L" />
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="label-caps">Ingredientes</div>
              <button onClick={addIngredient} className="ifl-btn-outline" data-testid="add-ingredient-btn">
                <Plus size={12} strokeWidth={2} /> Ingrediente
              </button>
            </div>

            <div className="ifl-card mb-6 overflow-hidden">
              <table className="ifl-table">
                <thead>
                  <tr>
                    <th>Ingrediente</th>
                    <th className="text-right">Qtde</th>
                    <th>Un.</th>
                    <th className="text-right">Custo unit.</th>
                    <th className="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(editing.ingredients || []).map((i, idx) => (
                    <tr key={idx} data-testid={`ingredient-row-${idx}`}>
                      <td>
                        <input className="ifl-input w-full" value={i.name} onChange={(e) => updateIngredient(idx, "name", e.target.value)} placeholder="Nome do ingrediente" />
                      </td>
                      <td className="text-right" style={{ minWidth: 90 }}>
                        <input type="number" step="0.001" className="ifl-input w-24 text-right" value={i.quantity} onChange={(e) => updateIngredient(idx, "quantity", e.target.value)} />
                      </td>
                      <td>
                        <input className="ifl-input w-16" value={i.unit} onChange={(e) => updateIngredient(idx, "unit", e.target.value)} placeholder="g/ml" />
                      </td>
                      <td className="text-right">
                        <input type="number" step="0.0001" className="ifl-input w-24 text-right" value={i.unit_cost} onChange={(e) => updateIngredient(idx, "unit_cost", e.target.value)} />
                      </td>
                      <td className="text-right font-display">{fmtBRL((Number(i.quantity) || 0) * (Number(i.unit_cost) || 0))}</td>
                      <td>
                        <button onClick={() => removeIngredient(idx)} className="p-1 rounded">
                          <X size={14} strokeWidth={1.5} style={{ color: "var(--ifl-danger)" }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(editing.ingredients || []).length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--ifl-muted)" }}>Sem ingredientes ainda. Clique em <strong>+ Ingrediente</strong>.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="ifl-card p-4">
                <div className="label-caps">Custo total</div>
                <div className="font-display text-xl mt-1">{fmtBRL(totalCost)}</div>
              </div>
              <div className="ifl-card p-4">
                <div className="label-caps">Preço de venda</div>
                <div className="font-display text-xl mt-1">{fmtBRL(editing.sale_price)}</div>
              </div>
              <div className="ifl-card p-4">
                <div className="label-caps">CMV teórico</div>
                <div className="font-display text-xl mt-1" style={{ color: cmvPct > 35 ? "var(--ifl-danger)" : "var(--ifl-text)" }}>{cmvPct ? fmtPct(cmvPct) : "—"}</div>
              </div>
            </div>

            <div className="mb-6">
              <div className="label-caps mb-1">Observações</div>
              <textarea className="ifl-input w-full" rows={3} value={editing.notes} onChange={(e) => setEditing(s => ({ ...s, notes: e.target.value }))} />
            </div>

            <div className="flex justify-end gap-2">
              <button className="ifl-btn-outline" onClick={() => setEditing(null)} data-testid="cancel-recipe-btn">Cancelar</button>
              <button className="ifl-btn" onClick={save} data-testid="save-recipe-btn">
                <Save size={14} strokeWidth={2} /> Salvar ficha
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
